import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  MapPinIcon,
  DocumentTextIcon,
  CameraIcon,
  WrenchScrewdriverIcon,
  ArchiveBoxIcon,
  DocumentPlusIcon,
  CheckCircleIcon,
  DocumentChartBarIcon,
  XMarkIcon,
  ChevronDownIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderPlusIcon,
  ChevronRightIcon as ChevronRightSmall,
  VideoCameraIcon,
  LinkIcon,
  ClipboardDocumentCheckIcon,
  PencilIcon,
  FolderArrowDownIcon,
} from '@heroicons/react/24/outline';
import { FolderIcon } from '@heroicons/react/24/solid';
import { supabase } from '../utils/supabaseClient';
import { generateShortId } from '../pages/FileDownloadPage';
import ReportGeneratorModal from './ReportGeneratorModal';
import { useReportGenerator } from '../hooks/useReportGenerator';

// --- CONSTANTS & HELPERS ---
const FILE_TYPES = [
    { value: 'raw_profile_data', label: 'Raw Profile Data', description: 'PPF files containing unfiltered pavement measurements', icon: ChartBarIcon, color: 'red', extensions: ['.ppf'] },
    { value: 'gps_data', label: 'GPS Trace Data', description: 'GPS coordinate files for mapping profile data', icon: MapPinIcon, color: 'green', extensions: ['.gpx', '.kml', '.kmz'] },
    { value: 'analysis_reports', label: 'Analysis Reports', description: 'ProVAL and ride quality analysis reports', icon: DocumentTextIcon, color: 'purple', extensions: ['.pdf'] },
    { value: 'proval_project', label: 'ProVAL Project Files', description: 'Complete ProVAL project bundles and configurations', icon: FolderIcon, color: 'indigo', extensions: ['.pvp', '.proval'] },
    { value: 'smoothness_calculations', label: 'Smoothness Calculations', description: 'Spreadsheet calculations for pay adjustments', icon: DocumentChartBarIcon, color: 'emerald', extensions: ['.xlsm', '.xlsx', '.xls'] },
    { value: 'qc_certification', label: 'QC & Certification', description: 'Quality control tests and equipment certifications', icon: CheckCircleIcon, color: 'teal', extensions: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'] },
    { value: 'agency_forms', label: 'Agency Forms & Submittals', description: 'Required agency submittal and correction forms', icon: DocumentIcon, color: 'orange', extensions: ['.pdf', '.doc', '.docx'] },
    { value: 'image', label: 'Roadway Imagery', description: 'Photos of pavement conditions', icon: CameraIcon, color: 'pink', extensions: ['.jpg', '.jpeg', '.png'] },
    { value: 'roadway_video', label: 'Roadway Video', description: 'Videos of pavement conditions', icon: VideoCameraIcon, color: 'pink', extensions: ['.mp4', '.avi', '.mov'] },
    { value: 'corrective_work', label: 'Corrective Work Documentation', description: 'Plans and documentation for corrective treatments', icon: WrenchScrewdriverIcon, color: 'red', extensions: ['.pdf', '.doc', '.docx', '.dwg', '.dxf'] },
    { value: 'compressed_archives', label: 'Compressed Archives', description: 'ZIP files containing multiple related documents', icon: ArchiveBoxIcon, color: 'gray', extensions: ['.zip', '.rar', '.7z'] },
    { value: 'supplemental_data', label: 'Supplemental Data', description: 'Additional project data and documentation', icon: DocumentPlusIcon, color: 'slate', extensions: ['.csv', '.txt', '.json', '.xml', '.dat'] }
];

const AUTO_GROUP_THRESHOLD = 10; // Auto-group when more than this many items
const ITEMS_PER_PAGE = 50; // Pagination for virtual folders

// System files that cannot be renamed or moved
const isSystemFile = (file) => {
  if (!file) return true;

  // Check by filename
  const filename = file.filename?.toLowerCase() || '';
  if (filename === 'maps.kml') return true;

  // Check by extension
  const ext = filename.split('.').pop();
  if (ext === 'ppf') return true;

  // Check by file type - system-generated types
  const systemTypes = ['raw_profile_data', 'gps_data', 'proval_project', 'image', 'roadway_video'];
  if (systemTypes.includes(file.file_type)) return true;

  return false;
};

const getFileTypeInfo = (typeValue) => FILE_TYPES.find(type => type.value === typeValue) || FILE_TYPES.find(t => t.value === 'supplemental_data');

const getColorClasses = (color) => ({
    red: 'bg-red-100 text-red-800 border-red-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    purple: 'bg-purple-100 text-purple-800 border-purple-200',
    indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    teal: 'bg-teal-100 text-teal-800 border-teal-200',
    orange: 'bg-orange-100 text-orange-800 border-orange-200',
    pink: 'bg-pink-100 text-pink-800 border-pink-200',
    gray: 'bg-gray-100 text-gray-800 border-gray-200',
    slate: 'bg-slate-100 text-slate-800 border-slate-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
}[color] || 'bg-gray-100 text-gray-800 border-gray-200');

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '--';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

// --- CHILD COMPONENTS ---

// Create Folder Menu Dropdown
const CreateFolderMenu = ({ onCreate }) => {
    const [folderName, setFolderName] = useState('');

    const handleCreate = (close) => {
        if (folderName.trim()) {
            onCreate(folderName.trim());
            setFolderName('');
            close();
        }
    };

    const handleCancel = (close) => {
        setFolderName('');
        close();
    };

    return (
        <Menu as="div" className="relative inline-block">
            {({ close }) => (
                <>
                    <MenuButton className="inline-flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium text-gray-700">
                        <FolderPlusIcon className="h-4 w-4 mr-2" />
                        New Folder
                    </MenuButton>
                    <MenuItems
                        transition
                        className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-md bg-white shadow-lg border border-gray-200 transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && folderName.trim()) {
                                e.preventDefault();
                                handleCreate(close);
                            }
                        }}
                    >
                        <div className="p-4 space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Folder Name
                                </label>
                                <input
                                    type="text"
                                    value={folderName}
                                    onChange={(e) => setFolderName(e.target.value)}
                                    placeholder="Enter folder name..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                    autoFocus
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleCancel(close)}
                                    className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleCreate(close)}
                                    disabled={!folderName.trim()}
                                    className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Create
                                </button>
                            </div>
                        </div>
                    </MenuItems>
                </>
            )}
        </Menu>
    );
};

// Media Viewer Modal
const MediaViewerModal = ({ files, currentIndex, onClose, onNavigate }) => {
    const [mediaUrl, setMediaUrl] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMedia = async () => {
            if (!files || files.length === 0) return;
            setLoading(true);
            try {
                const currentFile = files[currentIndex];
                const { data, error } = await supabase.storage.from('session-files').createSignedUrl(currentFile.storage_path, 3600);
                if (error) throw error;
                setMediaUrl(data.signedUrl);
            } catch (error) {
                console.error("Error creating signed URL:", error);
                setMediaUrl(null);
            } finally {
                setLoading(false);
            }
        };
        fetchMedia();
    }, [currentIndex, files]);

    const handlePrev = useCallback(() => onNavigate('prev'), [onNavigate]);
    const handleNext = useCallback(() => onNavigate('next'), [onNavigate]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handlePrev, handleNext, onClose]);

    if (!files || files.length === 0) return null;

    const currentFile = files[currentIndex];
    const isVideo = currentFile.file_type === 'roadway_video';

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={onClose}>
            <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-300 z-20">
                    <XMarkIcon className="h-8 w-8" />
                </button>
                {files.length > 1 && (
                    <>
                        <button onClick={handlePrev} className="absolute left-4 text-white hover:text-gray-300 z-20 p-2 bg-black/30 rounded-full">
                            <ChevronLeftIcon className="h-8 w-8" />
                        </button>
                        <button onClick={handleNext} className="absolute right-4 text-white hover:text-gray-300 z-20 p-2 bg-black/30 rounded-full">
                            <ChevronRightIcon className="h-8 w-8" />
                        </button>
                    </>
                )}
                <div className="relative max-w-full max-h-full flex items-center justify-center">
                    {loading ? (
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
                    ) : mediaUrl ? (
                        isVideo ? (
                            <video src={mediaUrl} controls className="max-w-full max-h-[90vh] rounded-md" />
                        ) : (
                            <img src={mediaUrl} alt={currentFile.filename} className="max-w-full max-h-[90vh] object-contain rounded-md" />
                        )
                    ) : (
                        <div className="text-white">Failed to load media.</div>
                    )}
                </div>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-center bg-black/50 px-4 py-2 rounded-md">
                    <p className="text-sm font-medium">{currentFile.filename}</p>
                    {files.length > 1 && <p className="text-xs text-gray-300">{currentIndex + 1} / {files.length}</p>}
                </div>
            </div>
        </div>,
        document.body
    );
};

// File Preview Modal
// Universal File Viewer Modal - Intelligent preview based on file type
const UniversalFileViewer = ({ file, onClose, onDownload, onDelete, onShare, onRename, onMove, canEdit }) => {
    const [fileUrl, setFileUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [textContent, setTextContent] = useState(null);
    
    const typeInfo = getFileTypeInfo(file.file_type);
    const TypeIcon = typeInfo.icon;
    
    // Determine the preview type based on file extension and type
    const getPreviewType = () => {
        const ext = file.filename.split('.').pop()?.toLowerCase();
        
        // Images
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext) || file.file_type === 'image') {
            return 'image';
        }
        // Videos
        if (['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext) || file.file_type === 'roadway_video') {
            return 'video';
        }
        // PDFs
        if (ext === 'pdf') {
            return 'pdf';
        }
        // Text files
        if (['txt', 'md', 'csv', 'json', 'xml', 'log', 'yml', 'yaml'].includes(ext)) {
            return 'text';
        }
        // Code files
        if (['js', 'jsx', 'ts', 'tsx', 'py', 'html', 'css', 'sql'].includes(ext)) {
            return 'code';
        }
        // Office documents - can't preview directly, show info
        if (['doc', 'docx', 'xls', 'xlsx', 'xlsm', 'ppt', 'pptx'].includes(ext)) {
            return 'office';
        }
        // Default - show file info
        return 'generic';
    };
    
    const previewType = getPreviewType();

    useEffect(() => {
        const fetchFileUrl = async () => {
            setLoading(true);
            try {
                const { data, error } = await supabase.storage
                    .from('session-files')
                    .createSignedUrl(file.storage_path, 3600);
                if (error) throw error;
                setFileUrl(data.signedUrl);
                
                // For text/code files, fetch the content
                if (previewType === 'text' || previewType === 'code') {
                    try {
                        const response = await fetch(data.signedUrl);
                        const text = await response.text();
                        setTextContent(text);
                    } catch (e) {
                        console.error('Failed to fetch text content:', e);
                    }
                }
            } catch (error) {
                console.error("Error creating signed URL:", error);
                setFileUrl(null);
            } finally {
                setLoading(false);
            }
        };
        fetchFileUrl();
    }, [file, previewType]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const renderPreview = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-red-600 border-t-transparent"></div>
                </div>
            );
        }

        if (!fileUrl) {
            return (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <XMarkIcon className="h-12 w-12 mb-2" />
                    <p>Failed to load file preview</p>
                </div>
            );
        }

        switch (previewType) {
            case 'image':
                return (
                    <div className="flex items-center justify-center bg-gray-900 rounded-sm overflow-hidden" style={{ minHeight: '400px', maxHeight: '70vh' }}>
                        <img 
                            src={fileUrl} 
                            alt={file.filename} 
                            className="max-w-full max-h-[70vh] object-contain"
                        />
                    </div>
                );
            
            case 'video':
                return (
                    <div className="bg-gray-900 rounded-sm overflow-hidden">
                        <video 
                            src={fileUrl} 
                            controls 
                            className="w-full max-h-[70vh]"
                            style={{ minHeight: '300px' }}
                        />
                    </div>
                );
            
            case 'pdf':
                return (
                    <div className="rounded-sm overflow-hidden border border-gray-200" style={{ height: '70vh' }}>
                        <iframe
                            src={`${fileUrl}#toolbar=1&navpanes=0`}
                            title={file.filename}
                            className="w-full h-full"
                            style={{ minHeight: '500px' }}
                        />
                    </div>
                );
            
            case 'text':
            case 'code':
                return (
                    <div className="rounded-sm border border-gray-200 bg-gray-50 overflow-hidden" style={{ maxHeight: '60vh' }}>
                        <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600">
                                {file.filename.split('.').pop()?.toUpperCase()} File
                            </span>
                            <span className="text-xs text-gray-500">
                                {textContent ? `${textContent.split('\n').length} lines` : ''}
                            </span>
                        </div>
                        <pre className="p-4 overflow-auto text-sm font-mono text-gray-800 whitespace-pre-wrap" style={{ maxHeight: 'calc(60vh - 40px)' }}>
                            {textContent || 'Loading...'}
                        </pre>
                    </div>
                );
            
            case 'office':
                return (
                    <div className="flex flex-col items-center justify-center py-12 px-6 bg-gray-50 rounded-sm border border-gray-200">
                        <TypeIcon className="h-16 w-16 text-gray-400 mb-4" />
                        <p className="text-gray-700 font-medium mb-2">Office Document</p>
                        <p className="text-sm text-gray-500 text-center mb-4">
                            Preview not available for this file type.<br />
                            Download to view in Microsoft Office or compatible application.
                        </p>
                        <button
                            onClick={() => onDownload(file)}
                            className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 text-sm font-medium"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                            Download File
                        </button>
                    </div>
                );
            
            default:
                return (
                    <div className="flex flex-col items-center justify-center py-12 px-6 bg-gray-50 rounded-sm border border-gray-200">
                        <TypeIcon className="h-16 w-16 text-gray-400 mb-4" />
                        <p className="text-gray-700 font-medium mb-2">{typeInfo.label}</p>
                        <p className="text-sm text-gray-500 text-center mb-4">
                            Preview not available for this file type.
                        </p>
                        <button
                            onClick={() => onDownload(file)}
                            className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 text-sm font-medium"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                            Download File
                        </button>
                    </div>
                );
        }
    };

    // Determine modal size based on preview type
    const getModalSize = () => {
        switch (previewType) {
            case 'image':
            case 'video':
            case 'pdf':
                return 'max-w-5xl';
            case 'text':
            case 'code':
                return 'max-w-4xl';
            default:
                return 'max-w-lg';
        }
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div className={`bg-white rounded-sm shadow-2xl w-full ${getModalSize()} my-8`} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <div className="flex items-center gap-3 min-w-0">
                        <TypeIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                        <div className="min-w-0">
                            <h3 className="text-base font-semibold text-gray-900 truncate" title={file.filename}>
                                {file.filename}
                            </h3>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getColorClasses(typeInfo.color)}`}>
                                    {typeInfo.label}
                                </span>
                                <span className="text-xs text-gray-500">{formatFileSize(file.file_size)}</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Preview Area */}
                <div className="p-5">
                    {renderPreview()}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="text-xs text-gray-500">
                        Added {formatDate(file.created_at)}
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Share Link */}
                        {onShare && (
                            <button
                                onClick={() => { onShare(file); }}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50"
                                title="Copy share link"
                            >
                                <LinkIcon className="h-4 w-4 mr-1.5" />
                                Share
                            </button>
                        )}
                        {/* Rename */}
                        {canEdit && onRename && (
                            <button
                                onClick={() => { onRename(file); onClose(); }}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50"
                                title="Rename file"
                            >
                                <PencilIcon className="h-4 w-4 mr-1.5" />
                                Rename
                            </button>
                        )}
                        {/* Move */}
                        {canEdit && onMove && (
                            <button
                                onClick={() => { onMove(file); onClose(); }}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50"
                                title="Move to folder"
                            >
                                <FolderArrowDownIcon className="h-4 w-4 mr-1.5" />
                                Move
                            </button>
                        )}
                        {/* Download */}
                        <button
                            onClick={() => onDownload(file)}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-sm hover:bg-gray-800"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                            Download
                        </button>
                        {/* Delete */}
                        <button
                            onClick={() => { onDelete(file); onClose(); }}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-sm hover:bg-red-100"
                            title="Delete file"
                        >
                            <TrashIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, sessionId, onUploadComplete, currentFolderId }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileType, setFileType] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files?.[0]) {
            const file = e.dataTransfer.files[0];
            setSelectedFile(file);
            autoDetectFileType(file);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            autoDetectFileType(file);
        }
    };

    const autoDetectFileType = (file) => {
        if (!file) return;
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        const matchingType = FILE_TYPES.find(t => t.extensions.includes(extension));
        if (matchingType) setFileType(matchingType.value);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            alert('Please select a file');
            return;
        }
        setUploading(true);
        try {
            // Use detected/selected file type, or default to supplemental_data
            const finalFileType = fileType || 'supplemental_data';
            const storagePath = `${sessionId}/${finalFileType}/${selectedFile.name}`;
            const { error: uploadError } = await supabase.storage.from('session-files').upload(storagePath, selectedFile, { upsert: true });
            if (uploadError) throw uploadError;

            const { error: dbError } = await supabase.from('session_files').insert({
                session_id: sessionId,
                filename: selectedFile.name,
                file_type: finalFileType,
                file_size: selectedFile.size,
                storage_path: storagePath,
                type: 'file',
                parent_id: currentFolderId
            });

            if (dbError) {
                await supabase.storage.from('session-files').remove([storagePath]);
                throw dbError;
            }
            onUploadComplete();
            handleClose();
        } catch (error) {
            console.error('Upload failed:', error);
            alert('Upload failed: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (uploading) return;
        setSelectedFile(null);
        setFileType('');
        onClose();
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 z-50">
            <div className="flex items-center justify-center min-h-screen p-4">
                <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b">
                        <h3 className="text-lg font-semibold">Upload File</h3>
                        <button onClick={handleClose} disabled={uploading} className="text-gray-400 hover:text-gray-600">
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="p-5 space-y-4">
                        {!selectedFile ? (
                            <div
                                className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors ${dragActive ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <CloudArrowUpIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                                <p className="text-sm font-medium text-gray-700 mb-1">Drop your file here</p>
                                <p className="text-xs text-gray-500 mb-3">or</p>
                                <input type="file" className="hidden" id="fileInput" onChange={handleFileSelect} />
                                <label htmlFor="fileInput" className="inline-flex items-center px-4 py-2 text-sm bg-gray-900 text-white rounded-md cursor-pointer hover:bg-gray-800">
                                    Browse Files
                                </label>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-sm">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <DocumentIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                                            <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => { setSelectedFile(null); setFileType(''); }} disabled={uploading} className="text-sm text-red-600 hover:text-red-700 font-medium">
                                        Remove
                                    </button>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        File Type <span className="text-gray-400 font-normal">(optional)</span>
                                    </label>
                                    <select
                                        value={fileType}
                                        onChange={(e) => setFileType(e.target.value)}
                                        disabled={uploading}
                                        className="w-full p-2.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    >
                                        <option value="">Auto-detect / Supplemental</option>
                                        {FILE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                    <p className="mt-1.5 text-xs text-gray-500">
                                        Leave blank to auto-categorize based on file extension
                                    </p>
                                </div>
                            </div>
                        )}
                        {uploading && (
                            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-sm">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                                <span className="text-sm font-medium text-red-800">Uploading...</span>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3 p-5 border-t bg-gray-50">
                        <button onClick={handleClose} disabled={uploading} className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 font-medium">
                            Cancel
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={!selectedFile || uploading}
                            className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                            {uploading ? 'Uploading...' : 'Upload'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

// Rename Modal Component
const RenameModal = ({ isOpen, onClose, item, onRename }) => {
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Get the original file extension (for files, not folders)
    const isFile = item?.type === 'file';
    const originalExtension = isFile && item?.filename?.includes('.')
        ? '.' + item.filename.split('.').pop().toLowerCase()
        : null;

    useEffect(() => {
        if (isOpen && item) {
            setNewName(item.filename);
            setError(null);
        }
    }, [isOpen, item]);

    // Validate that file extension is preserved
    const validateExtension = (name) => {
        if (!isFile || !originalExtension) return true;
        const newExt = name.includes('.') ? '.' + name.split('.').pop().toLowerCase() : null;
        return newExt === originalExtension;
    };

    const hasValidExtension = validateExtension(newName);

    const handleRename = async () => {
        if (!newName.trim() || newName === item.filename) {
            onClose();
            return;
        }

        // Check for extension validation
        if (isFile && !hasValidExtension) {
            setError(`File extension must remain "${originalExtension}"`);
            return;
        }

        setSaving(true);
        setError(null);

        try {
            await onRename(item, newName.trim());
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to rename');
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && newName.trim() && hasValidExtension) {
            handleRename();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    if (!isOpen || !item) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-sm shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900">
                        Rename {item.type === 'folder' ? 'Folder' : 'File'}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-5">
                    {error && (
                        <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded-sm">
                            {error}
                        </div>
                    )}
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className={`w-full px-3 py-2 border rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm ${
                            !hasValidExtension ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        autoFocus
                        disabled={saving}
                    />
                    {isFile && originalExtension && (
                        <p className={`mt-1.5 text-xs ${!hasValidExtension ? 'text-red-600' : 'text-gray-500'}`}>
                            {!hasValidExtension
                                ? `File extension "${originalExtension}" is required`
                                : `File extension "${originalExtension}" will be preserved`
                            }
                        </p>
                    )}
                </div>
                <div className="flex gap-3 px-5 py-4 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-100 font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleRename}
                        disabled={saving || !newName.trim() || newName === item.filename || !hasValidExtension}
                        className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                        {saving ? 'Saving...' : 'Rename'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// Move to Folder Modal Component
const MoveToFolderModal = ({ isOpen, onClose, item, folders, currentFolderId, onMove }) => {
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setSelectedFolderId(null);
            setError(null);
        }
    }, [isOpen]);

    const handleMove = async () => {
        setSaving(true);
        setError(null);

        try {
            await onMove(item, selectedFolderId);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to move');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !item) return null;

    // Filter out the current item if it's a folder (can't move into itself)
    const availableFolders = folders.filter(f => f.id !== item.id);

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-sm shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900">Move to Folder</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-5">
                    {error && (
                        <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded-sm">
                            {error}
                        </div>
                    )}
                    <p className="text-sm text-gray-600 mb-3">
                        Moving: <span className="font-medium text-gray-900">{item.filename}</span>
                    </p>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Destination</label>
                    <div className="border border-gray-300 rounded-sm overflow-hidden max-h-60 overflow-y-auto">
                        {/* Root option */}
                        <button
                            onClick={() => setSelectedFolderId(null)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                                selectedFolderId === null && currentFolderId !== null ? 'bg-red-50 border-l-2 border-red-600' : ''
                            } ${currentFolderId === null ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={currentFolderId === null}
                        >
                            <FolderIcon className="h-5 w-5 text-yellow-500" />
                            <span className="text-sm font-medium text-gray-900">Session Files (Root)</span>
                            {currentFolderId === null && (
                                <span className="ml-auto text-xs text-gray-400">Current</span>
                            )}
                        </button>
                        {availableFolders.length === 0 && currentFolderId === null ? (
                            <div className="px-4 py-6 text-center text-sm text-gray-500">
                                No folders available. Create a folder first.
                            </div>
                        ) : (
                            availableFolders.map(folder => (
                                <button
                                    key={folder.id}
                                    onClick={() => setSelectedFolderId(folder.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-t border-gray-100 ${
                                        selectedFolderId === folder.id ? 'bg-red-50 border-l-2 border-red-600' : ''
                                    } ${folder.id === currentFolderId ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={folder.id === currentFolderId}
                                >
                                    <FolderIcon className="h-5 w-5 text-yellow-500" />
                                    <span className="text-sm font-medium text-gray-900">{folder.filename}</span>
                                    {folder.id === currentFolderId && (
                                        <span className="ml-auto text-xs text-gray-400">Current</span>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>
                <div className="flex gap-3 px-5 py-4 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-100 font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleMove}
                        disabled={saving || (selectedFolderId === null && currentFolderId === null) || selectedFolderId === currentFolderId}
                        className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                        {saving ? 'Moving...' : 'Move'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// Context Menu / Actions Dropdown Component
const ActionMenu = ({ isOpen, position, onClose, item, isFolder, canEdit, onView, onDownload, onShare, onRename, onMove, onDelete }) => {
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                onClose();
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const menuItems = [];

    // File-specific actions
    if (!isFolder) {
        menuItems.push(
            { icon: EyeIcon, label: 'View details', action: () => { onView(item); onClose(); } },
            { icon: ArrowDownTrayIcon, label: 'Download', action: () => { onDownload(item); onClose(); } },
            { icon: LinkIcon, label: 'Copy share link', action: () => { onShare(item); onClose(); } },
        );
    }

    // Editable actions (for non-system files and folders)
    if (canEdit) {
        if (menuItems.length > 0) menuItems.push({ divider: true });
        menuItems.push(
            { icon: PencilIcon, label: 'Rename', action: () => { onRename(item); onClose(); } },
            { icon: FolderArrowDownIcon, label: 'Move to folder', action: () => { onMove(item); onClose(); } },
        );
    }

    // Delete action
    if (menuItems.length > 0) menuItems.push({ divider: true });
    menuItems.push(
        { icon: TrashIcon, label: 'Delete', action: () => { onDelete(item); onClose(); }, danger: true },
    );

    return ReactDOM.createPortal(
        <div
            ref={menuRef}
            className="fixed z-50 bg-white rounded-sm shadow-lg border border-gray-200 py-1 min-w-[180px]"
            style={{ top: position.y, left: position.x }}
        >
            {menuItems.map((menuItem, idx) =>
                menuItem.divider ? (
                    <div key={`divider-${idx}`} className="my-1 border-t border-gray-100" />
                ) : (
                    <button
                        key={menuItem.label}
                        onClick={menuItem.action}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors ${
                            menuItem.danger
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <menuItem.icon className="h-4 w-4" />
                        {menuItem.label}
                    </button>
                )
            )}
        </div>,
        document.body
    );
};

// Unified Row Component - handles both folders and files
const UnifiedRow = ({ item, isFolder, isVirtualFolder, isSelected, onSelect, onClick, onDownload, onShare, onDelete, onView, onRename, onMove, canEdit }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [contextMenu, setContextMenu] = useState({ isOpen: false, x: 0, y: 0 });
    const actionButtonRef = useRef(null);

    const handleContextMenu = (e) => {
        if (isVirtualFolder) return;
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ isOpen: true, x: e.clientX, y: e.clientY });
    };

    const handleActionClick = (e) => {
        e.stopPropagation();
        const rect = actionButtonRef.current?.getBoundingClientRect();
        if (rect) {
            setContextMenu({ isOpen: true, x: rect.right - 180, y: rect.bottom + 4 });
        }
    };

    const closeContextMenu = () => setContextMenu({ isOpen: false, x: 0, y: 0 });

    const typeInfo = isFolder
        ? { icon: FolderIcon, label: 'Folder', color: 'yellow' }
        : getFileTypeInfo(item.file_type);

    const TypeIcon = isVirtualFolder
        ? (item.virtualType === 'image' ? CameraIcon : VideoCameraIcon)
        : typeInfo.icon;

    const iconColorClass = isFolder || isVirtualFolder
        ? 'text-yellow-500'
        : 'text-gray-400';

    return (
        <>
            <tr
                className={`group transition-colors cursor-pointer ${isSelected ? 'bg-red-50' : 'hover:bg-gray-50'}`}
                onClick={onClick}
                onContextMenu={handleContextMenu}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Checkbox */}
                <td className="w-12 px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {!isVirtualFolder && (
                        <input
                            type="checkbox"
                            className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                            checked={isSelected}
                            onChange={(e) => onSelect(item.id, e.target.checked)}
                        />
                    )}
                </td>

                {/* Name */}
                <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                        <TypeIcon className={`h-5 w-5 flex-shrink-0 ${iconColorClass}`} />
                        <span className="text-sm font-medium text-gray-900 truncate" title={item.filename}>
                            {item.filename}
                        </span>
                        {isVirtualFolder && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                {item.fileCount}
                            </span>
                        )}
                    </div>
                </td>

                {/* Type Badge */}
                <td className="px-4 py-3">
                    {isVirtualFolder ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-amber-100 text-amber-800">
                            Auto-grouped
                        </span>
                    ) : (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${getColorClasses(typeInfo.color)}`}>
                            {typeInfo.label}
                        </span>
                    )}
                </td>

                {/* Size */}
                <td className="px-4 py-3 text-sm text-gray-500">
                    {isVirtualFolder ? formatFileSize(item.totalSize) : formatFileSize(item.file_size)}
                </td>

                {/* Date */}
                <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(item.created_at || item.latestDate)}
                </td>

                {/* Actions */}
                <td className="w-32 px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                        {!isVirtualFolder && (
                            <button
                                ref={actionButtonRef}
                                onClick={handleActionClick}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-all ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                            >
                                Actions
                                <ChevronDownIcon className="h-3 w-3" />
                            </button>
                        )}
                        {(isFolder || isVirtualFolder) && (
                            <ChevronRightSmall className={`h-4 w-4 text-gray-400 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`} />
                        )}
                    </div>
                </td>
            </tr>

            {/* Context Menu */}
            <ActionMenu
                isOpen={contextMenu.isOpen}
                position={{ x: contextMenu.x, y: contextMenu.y }}
                onClose={closeContextMenu}
                item={item}
                isFolder={isFolder}
                canEdit={canEdit}
                onView={onView}
                onDownload={onDownload}
                onShare={onShare}
                onRename={onRename}
                onMove={onMove}
                onDelete={onDelete}
            />
        </>
    );
};

// Section Header Row
const SectionHeader = ({ title, count, selectedCount, onSelectAll, allSelected, showSelectAll }) => (
    <tr className="bg-gray-50 border-y border-gray-200">
        <td colSpan={6} className="px-4 py-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {showSelectAll && (
                        <input
                            type="checkbox"
                            className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                            checked={allSelected}
                            onChange={onSelectAll}
                        />
                    )}
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {title}
                    </span>
                    <span className="text-xs text-gray-400">({count})</span>
                </div>
                {selectedCount > 0 && (
                    <span className="text-xs font-medium text-red-600">
                        {selectedCount} selected
                    </span>
                )}
            </div>
        </td>
    </tr>
);

// Pagination Component
const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    // Generate page numbers to show
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    if (totalPages <= 1) return null;

    return (
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
            <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of{' '}
                <span className="font-medium">{totalItems}</span> files
            </div>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    <ChevronLeftIcon className="h-4 w-4" />
                </button>
                
                {getPageNumbers().map((page, idx) => (
                    page === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-400">...</span>
                    ) : (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`px-3 py-1 text-sm rounded transition-colors ${
                                currentPage === page
                                    ? 'bg-red-600 text-white font-medium'
                                    : 'text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {page}
                        </button>
                    )
                ))}
                
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    <ChevronRightIcon className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const SessionFiles = ({ sessionId, session, project, projectConfig, segments, events }) => {
    // Core State
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentFolderId, setCurrentFolderId] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'Session Files' }]);

    // Virtual folder state (for auto-grouped images/videos)
    const [virtualFolderType, setVirtualFolderType] = useState(null); // 'image' or 'roadway_video'
    const [currentPage, setCurrentPage] = useState(1);

    // UI Feature State
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [previewFile, setPreviewFile] = useState(null);

    // Rename and Move modal state
    const [renameItem, setRenameItem] = useState(null);
    const [moveItem, setMoveItem] = useState(null);
    const [allFolders, setAllFolders] = useState([]);

    const [showReportModal, setShowReportModal] = useState(false);
    const { generate: generateReport } = useReportGenerator();

    const handleGenerateReport = async (options) => {
        try {
            const result = await generateReport(options);
            if (result.message) {
                alert(result.message);
            }
            return result;
        } catch (error) {
            console.error('Report generation failed:', error);
            throw error;
        }
    };

    // Data Fetching
    const loadItems = useCallback(async () => {
        setLoading(true);
        setSelectedItems([]);
        try {
            let query = supabase.from('session_files').select('*').eq('session_id', sessionId);

            if (virtualFolderType) {
                // Loading items for a virtual folder - get all items of that type in this session
                query = query.eq('file_type', virtualFolderType).eq('type', 'file');
            } else if (currentFolderId === null) {
                query = query.is('parent_id', null);
            } else {
                query = query.eq('parent_id', currentFolderId);
            }

            const { data, error } = await query
                .order('type', { ascending: false })
                .order('filename', { ascending: true });
            if (error) throw error;
            setItems(data || []);
        } catch (error) {
            console.error('Error loading items:', error);
            alert('Error: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [sessionId, currentFolderId, virtualFolderType]);

    useEffect(() => {
        if (sessionId) loadItems();
    }, [sessionId, currentFolderId, virtualFolderType, loadItems]);

    // Load all folders for move modal
    const loadAllFolders = useCallback(async () => {
        if (!sessionId) return;
        try {
            const { data, error } = await supabase
                .from('session_files')
                .select('*')
                .eq('session_id', sessionId)
                .eq('type', 'folder')
                .order('filename', { ascending: true });
            if (error) throw error;
            setAllFolders(data || []);
        } catch (error) {
            console.error('Error loading folders:', error);
        }
    }, [sessionId]);

    useEffect(() => {
        loadAllFolders();
    }, [loadAllFolders]);

    // Computed data with virtual folders
    const { folders, files, virtualFolders } = useMemo(() => {
        const realFolders = items.filter(item => item.type === 'folder');
        let realFiles = items.filter(item => item.type === 'file');
        const virtuals = [];

        // Only create virtual folders when not already inside one
        if (!virtualFolderType) {
            // Check for images that should be auto-grouped
            const images = realFiles.filter(f => f.file_type === 'image');
            if (images.length > AUTO_GROUP_THRESHOLD) {
                virtuals.push({
                    id: 'virtual-images',
                    filename: 'Roadway Imagery',
                    virtualType: 'image',
                    fileCount: images.length,
                    totalSize: images.reduce((sum, f) => sum + (f.file_size || 0), 0),
                    latestDate: images.reduce((latest, f) => {
                        const d = new Date(f.created_at);
                        return d > latest ? d : latest;
                    }, new Date(0))
                });
                // Remove images from main file list
                realFiles = realFiles.filter(f => f.file_type !== 'image');
            }

            // Check for videos that should be auto-grouped
            const videos = realFiles.filter(f => f.file_type === 'roadway_video');
            if (videos.length > AUTO_GROUP_THRESHOLD) {
                virtuals.push({
                    id: 'virtual-videos',
                    filename: 'Roadway Videos',
                    virtualType: 'roadway_video',
                    fileCount: videos.length,
                    totalSize: videos.reduce((sum, f) => sum + (f.file_size || 0), 0),
                    latestDate: videos.reduce((latest, f) => {
                        const d = new Date(f.created_at);
                        return d > latest ? d : latest;
                    }, new Date(0))
                });
                realFiles = realFiles.filter(f => f.file_type !== 'roadway_video');
            }
        }

        return { folders: realFolders, files: realFiles, virtualFolders: virtuals };
    }, [items, virtualFolderType]);

    const mediaFiles = useMemo(() =>
        items.filter(item => item.type === 'file' && (item.file_type === 'image' || item.file_type === 'roadway_video')),
        [items]
    );

    const allFoldersAndVirtuals = useMemo(() => [...folders, ...virtualFolders], [folders, virtualFolders]);

    // Pagination for virtual folders
    const totalPages = useMemo(() => {
        if (virtualFolderType && files.length > 0) {
            return Math.ceil(files.length / ITEMS_PER_PAGE);
        }
        return 1;
    }, [virtualFolderType, files.length]);

    const paginatedFiles = useMemo(() => {
        if (virtualFolderType && files.length > ITEMS_PER_PAGE) {
            const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
            return files.slice(startIndex, startIndex + ITEMS_PER_PAGE);
        }
        return files;
    }, [files, currentPage, virtualFolderType]);

    // Reset pagination when changing folders
    useEffect(() => {
        setCurrentPage(1);
    }, [currentFolderId, virtualFolderType]);

    // Handlers
    const handleCreateFolder = async (folderName) => {
        if (!folderName?.trim()) return;
        try {
            const { error } = await supabase.from('session_files').insert({
                session_id: sessionId,
                filename: folderName.trim(),
                type: 'folder',
                parent_id: currentFolderId,
                file_size: 0,
                storage_path: '',
                file_type: 'folder'
            });
            if (error) throw error;
            loadItems();
        } catch (error) {
            console.error('Failed to create folder:', error);
            alert('Error: ' + error.message);
        }
    };

    const handleNavigate = (folder) => {
        if (folder.virtualType) {
            // Entering a virtual folder
            setVirtualFolderType(folder.virtualType);
            setBreadcrumbs(p => [...p, { id: folder.id, name: folder.filename, isVirtual: true, virtualType: folder.virtualType }]);
        } else {
            setCurrentFolderId(folder.id);
            setBreadcrumbs(p => [...p, { id: folder.id, name: folder.filename }]);
        }
    };

    const handleBreadcrumbClick = (crumb, index) => {
        if (crumb.isVirtual) {
            // Going back to a virtual folder
            setVirtualFolderType(crumb.virtualType);
            setCurrentFolderId(null);
        } else {
            setVirtualFolderType(null);
            setCurrentFolderId(crumb.id);
        }
        setBreadcrumbs(p => p.slice(0, index + 1));
    };

    const handleSelectItem = (itemId, isChecked) => {
        setSelectedItems(p => isChecked ? [...p, itemId] : p.filter(id => id !== itemId));
    };

    const handleSelectAllFiles = (e) => {
        // Select/deselect only the currently visible (paginated) files
        const visibleFileIds = paginatedFiles.map(f => f.id);
        if (e.target.checked) {
            setSelectedItems(p => [...new Set([...p, ...visibleFileIds])]);
        } else {
            const visibleSet = new Set(visibleFileIds);
            setSelectedItems(p => p.filter(id => !visibleSet.has(id)));
        }
    };

    const handleFileClick = (file) => {
        // In virtual folders (image/video galleries), use the gallery viewer for navigation
        if (virtualFolderType && (file.file_type === 'image' || file.file_type === 'roadway_video')) {
            const index = mediaFiles.findIndex(f => f.id === file.id);
            if (index !== -1) {
                setCurrentMediaIndex(index);
                setMediaViewerOpen(true);
                return;
            }
        }
        // For all other cases, use the universal file viewer
        setPreviewFile(file);
    };

    const handleMediaViewerNavigate = (direction) => {
        setCurrentMediaIndex(p => direction === 'next' ? (p + 1) % mediaFiles.length : (p - 1 + mediaFiles.length) % mediaFiles.length);
    };

    const handleDelete = async (itemsToDelete) => {
        const toDelete = Array.isArray(itemsToDelete) ? itemsToDelete : [itemsToDelete];
        const filesToDelete = items.filter(i => toDelete.includes(i.id) && i.type === 'file' && i.storage_path);
        const storagePathsToDelete = filesToDelete.map(i => i.storage_path);

        try {
            if (storagePathsToDelete.length > 0) {
                const { error: storageError } = await supabase.storage.from('session-files').remove(storagePathsToDelete);
                if (storageError) throw storageError;
            }
            const { error: dbError } = await supabase.from('session_files').delete().in('id', toDelete);
            if (dbError) throw dbError;
            loadItems();
        } catch (error) {
            console.error('Delete failed:', error);
            alert('Error: ' + error.message);
        }
    };

    const handleBulkDelete = () => {
        if (selectedItems.length > 0 && window.confirm(`Delete ${selectedItems.length} item(s)? This cannot be undone.`)) {
            handleDelete(selectedItems);
        }
    };

    const handleSingleDelete = (item) => {
        if (window.confirm(`Delete "${item.filename}"? This cannot be undone.`)) {
            handleDelete([item.id]);
        }
    };

    const handleDownload = async (file) => {
        try {
            const { data, error } = await supabase.storage.from('session-files').createSignedUrl(file.storage_path, 3600);
            if (error) throw error;
            const link = document.createElement('a');
            link.href = data.signedUrl;
            link.download = file.filename;
            link.click();
        } catch (e) {
            alert('Download failed.');
        }
    };

    const handleRename = async (item, newName) => {
        if (!newName.trim() || newName === item.filename) return;

        try {
            const { error } = await supabase
                .from('session_files')
                .update({ filename: newName.trim() })
                .eq('id', item.id);

            if (error) throw error;
            loadItems();
            loadAllFolders();
        } catch (error) {
            console.error('Rename failed:', error);
            throw new Error('Failed to rename. Please try again.');
        }
    };

    const handleMove = async (item, newParentId) => {
        if (newParentId === item.parent_id) return;

        try {
            const { error } = await supabase
                .from('session_files')
                .update({ parent_id: newParentId })
                .eq('id', item.id);

            if (error) throw error;
            loadItems();
        } catch (error) {
            console.error('Move failed:', error);
            throw new Error('Failed to move. Please try again.');
        }
    };

    const [shareToast, setShareToast] = useState(null);

    const handleShare = async (file, expiryDays = 7) => {
        try {
            // Generate a signed URL that lasts as long as the share link
            const expirySeconds = expiryDays * 24 * 60 * 60;
            const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                .from('session-files')
                .createSignedUrl(file.storage_path, expirySeconds);

            if (signedUrlError) throw signedUrlError;

            // Generate a short ID and store in database
            const shortId = generateShortId();
            const expiresAt = new Date(Date.now() + (expiryDays * 24 * 60 * 60 * 1000)).toISOString();

            const { error: insertError } = await supabase
                .from('file_shares')
                .insert({
                    short_id: shortId,
                    storage_path: file.storage_path,
                    filename: file.filename,
                    file_size: file.file_size,
                    file_type: file.file_type,
                    signed_url: signedUrlData.signedUrl,
                    expires_at: expiresAt,
                });

            if (insertError) throw insertError;

            const shareUrl = `${window.location.origin}/file/${shortId}`;
            await navigator.clipboard.writeText(shareUrl);

            setShareToast({ filename: file.filename, expiryDays });
            setTimeout(() => setShareToast(null), 3000);
        } catch (e) {
            console.error('Share failed:', e);
            alert('Failed to generate share link.');
        }
    };

    const selectedFileCount = paginatedFiles.filter(f => selectedItems.includes(f.id)).length;
    const allFilesSelected = paginatedFiles.length > 0 && paginatedFiles.every(f => selectedItems.includes(f.id));

    return (
        <div className="bg-white rounded-sm shadow-sm relative flex-1 flex flex-col min-h-0">
            {/* Header - matches FixedIntervalChart style */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 h-20 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900">Session Files</h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {items.length} items{selectedItems.length > 0 && ` · ${selectedItems.length} selected`}
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        {selectedItems.length > 0 && (
                            <button
                                onClick={handleBulkDelete}
                                className="inline-flex items-center px-3 py-2 text-sm bg-red-50 text-red-700 rounded-md hover:bg-red-100 font-medium"
                            >
                                <TrashIcon className="h-4 w-4 mr-1.5" />
                                Delete ({selectedItems.length})
                            </button>
                        )}
                        {!virtualFolderType && <CreateFolderMenu onCreate={handleCreateFolder} />}
                        <button
                            onClick={() => setShowReportModal(true)}
                            disabled={!session || !segments?.length}
                            className="inline-flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <DocumentChartBarIcon className="h-4 w-4 mr-1.5" />
                            Generate Report
                        </button>
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="inline-flex items-center px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
                        >
                            <CloudArrowUpIcon className="h-4 w-4 mr-1.5" />
                            Upload
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-auto min-h-0">
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-red-600 border-t-transparent"></div>
                    </div>
                ) : items.length === 0 && virtualFolders.length === 0 ? (
                    <div>
                        {/* Breadcrumbs Row */}
                        {breadcrumbs.length > 1 && (
                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                                <nav className="flex items-center space-x-1 text-sm">
                                    {breadcrumbs.map((crumb, index) => (
                                        <div key={crumb.id || 'root'} className="flex items-center">
                                            {index > 0 && <ChevronRightSmall className="h-4 w-4 text-gray-400 mx-1" />}
                                            <button
                                                onClick={() => handleBreadcrumbClick(crumb, index)}
                                                disabled={index === breadcrumbs.length - 1}
                                                className={`px-2 py-1 rounded transition-colors ${
                                                    index === breadcrumbs.length - 1
                                                        ? 'font-medium text-gray-900'
                                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                {crumb.name}
                                            </button>
                                        </div>
                                    ))}
                                </nav>
                            </div>
                        )}
                        <div className="p-12 text-center">
                            <FolderIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <h3 className="text-base font-semibold text-gray-900 mb-1">This folder is empty</h3>
                            <p className="text-sm text-gray-500 mb-5">Upload files or create a folder to get started</p>
                            <div className="flex items-center justify-center gap-2">
                                <CreateFolderMenu onCreate={handleCreateFolder} />
                                <button
                                    onClick={() => setShowUploadModal(true)}
                                    className="inline-flex items-center px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Upload File
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-hidden">
                        {/* Breadcrumbs Row */}
                        {breadcrumbs.length > 1 && (
                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                                <nav className="flex items-center space-x-1 text-sm">
                                    {breadcrumbs.map((crumb, index) => (
                                        <div key={crumb.id || 'root'} className="flex items-center">
                                            {index > 0 && <ChevronRightSmall className="h-4 w-4 text-gray-400 mx-1" />}
                                            <button
                                                onClick={() => handleBreadcrumbClick(crumb, index)}
                                                disabled={index === breadcrumbs.length - 1}
                                                className={`px-2 py-1 rounded transition-colors ${
                                                    index === breadcrumbs.length - 1
                                                        ? 'font-medium text-gray-900'
                                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                {crumb.name}
                                            </button>
                                        </div>
                                    ))}
                                </nav>
                            </div>
                        )}
                        <table className="min-w-full">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="w-12 px-4 py-3"></th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Size</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Modified</th>
                                    <th className="w-32 px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {/* Folders Section */}
                                {allFoldersAndVirtuals.length > 0 && (
                                    <>
                                        <SectionHeader
                                            title="Folders"
                                            count={allFoldersAndVirtuals.length}
                                            selectedCount={0}
                                            showSelectAll={false}
                                        />
                                        {folders.map(folder => (
                                            <UnifiedRow
                                                key={folder.id}
                                                item={folder}
                                                isFolder={true}
                                                isVirtualFolder={false}
                                                isSelected={selectedItems.includes(folder.id)}
                                                onSelect={handleSelectItem}
                                                onClick={() => handleNavigate(folder)}
                                                onDownload={handleDownload}
                                                onShare={handleShare}
                                                onDelete={handleSingleDelete}
                                                onView={setPreviewFile}
                                                onRename={() => setRenameItem(folder)}
                                                onMove={() => setMoveItem(folder)}
                                                canEdit={true}
                                            />
                                        ))}
                                        {virtualFolders.map(vf => (
                                            <UnifiedRow
                                                key={vf.id}
                                                item={vf}
                                                isFolder={false}
                                                isVirtualFolder={true}
                                                isSelected={false}
                                                onSelect={() => {}}
                                                onClick={() => handleNavigate(vf)}
                                                onDownload={() => {}}
                                                onShare={() => {}}
                                                onDelete={() => {}}
                                                onView={() => {}}
                                                onRename={() => {}}
                                                onMove={() => {}}
                                                canEdit={false}
                                            />
                                        ))}
                                    </>
                                )}

                                {/* Files Section */}
                                {files.length > 0 && (
                                    <>
                                        <SectionHeader
                                            title={virtualFolderType ? "Files" : "Files"}
                                            count={files.length}
                                            selectedCount={selectedFileCount}
                                            onSelectAll={handleSelectAllFiles}
                                            allSelected={allFilesSelected}
                                            showSelectAll={true}
                                        />
                                        {paginatedFiles.map(file => (
                                            <UnifiedRow
                                                key={file.id}
                                                item={file}
                                                isFolder={false}
                                                isVirtualFolder={false}
                                                isSelected={selectedItems.includes(file.id)}
                                                onSelect={handleSelectItem}
                                                onClick={() => handleFileClick(file)}
                                                onDownload={handleDownload}
                                                onShare={handleShare}
                                                onDelete={handleSingleDelete}
                                                onView={setPreviewFile}
                                                onRename={() => setRenameItem(file)}
                                                onMove={() => setMoveItem(file)}
                                                canEdit={!isSystemFile(file)}
                                            />
                                        ))}
                                    </>
                                )}
                            </tbody>
                        </table>
                        
                        {/* Pagination for virtual folders */}
                        {virtualFolderType && totalPages > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={files.length}
                                itemsPerPage={ITEMS_PER_PAGE}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* Modals */}
            <UploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                sessionId={sessionId}
                onUploadComplete={loadItems}
                currentFolderId={currentFolderId}
            />

            {mediaViewerOpen && (
                <MediaViewerModal
                    files={mediaFiles}
                    currentIndex={currentMediaIndex}
                    onClose={() => setMediaViewerOpen(false)}
                    onNavigate={handleMediaViewerNavigate}
                />
            )}

            {previewFile && (
                <UniversalFileViewer
                    file={previewFile}
                    onClose={() => setPreviewFile(null)}
                    onDownload={handleDownload}
                    onDelete={handleSingleDelete}
                    onShare={handleShare}
                    onRename={() => { setPreviewFile(null); setRenameItem(previewFile); }}
                    onMove={() => { setPreviewFile(null); setMoveItem(previewFile); }}
                    canEdit={!isSystemFile(previewFile)}
                />
            )}

            {showReportModal && (
                <ReportGeneratorModal
                    isOpen={showReportModal}
                    onClose={() => setShowReportModal(false)}
                    session={session}
                    project={project}
                    projectConfig={projectConfig}
                    segments={segments}
                    events={events}
                    onGenerate={handleGenerateReport}
                />
            )}

            {/* Rename Modal */}
            <RenameModal
                isOpen={!!renameItem}
                onClose={() => setRenameItem(null)}
                item={renameItem}
                onRename={handleRename}
            />

            {/* Move to Folder Modal */}
            <MoveToFolderModal
                isOpen={!!moveItem}
                onClose={() => setMoveItem(null)}
                item={moveItem}
                folders={allFolders}
                currentFolderId={currentFolderId}
                onMove={handleMove}
            />

            {/* Share Link Toast */}
            {shareToast && (
                <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2 fade-in duration-200">
                    <div className="bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
                        <ClipboardDocumentCheckIcon className="h-5 w-5 text-green-400" />
                        <div>
                            <p className="text-sm font-medium">Link copied to clipboard</p>
                            <p className="text-xs text-gray-400">
                                {shareToast.filename} · Expires in {shareToast.expiryDays} days
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SessionFiles;